// Sidebar + topbar layout. Adding a new admin page = one entry in
// this nav array + one <Route> in main.tsx.
//
// PX-73: each NAV item declares the permissions that should reveal
// it. `hasAnyPerm` means "show if the profile carries ANY of these"
// — admins (wildcard `*`) always satisfy that. Curators / custom
// roles only see entries that match their concrete permissions.

import { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { request } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { hasAnyPerm } from '@/lib/perms'
import { PERM, type AdminSupportStats, type Permission } from '@/types/api'

const NAV: { to: string; label: string; requiredPerms: Permission[] }[] = [
  { to: '/',            label: 'Dashboard',    requiredPerms: [PERM.users_read] },
  { to: '/users',       label: 'Users',        requiredPerms: [PERM.users_read] },
  { to: '/support',     label: 'Support',      requiredPerms: [PERM.support_read] },
  { to: '/roles',       label: 'Roles',        requiredPerms: [PERM.roles_read] },
  { to: '/photoshoots', label: 'Photoshoots',  requiredPerms: [PERM.photoshoots_read] },
  { to: '/catalog',     label: 'Catalog',      requiredPerms: [PERM.models_read, PERM.plans_read] },
  { to: '/monetization', label: 'Monetization', requiredPerms: [PERM.plans_read] },
  { to: '/login-carousel', label: 'Login carousel', requiredPerms: [PERM.login_images_read] },
  { to: '/paywall-carousel', label: 'Paywall carousel', requiredPerms: [PERM.paywall_images_read] },
  { to: '/fal-models',  label: 'fal.ai',       requiredPerms: [PERM.fal_models_read] },
  { to: '/generations', label: 'Generations',  requiredPerms: [PERM.generations_read] },
  { to: '/audit-log',   label: 'Audit log',    requiredPerms: [PERM.audit_read] },
  { to: '/cron',        label: 'Cron',         requiredPerms: [PERM.cron_read] },
  { to: '/app-review',  label: 'App Review',   requiredPerms: [PERM.app_review_read] },
  { to: '/settings',    label: 'Settings',     requiredPerms: [PERM.settings_write] },
  { to: '/profile',     label: 'Profile',      requiredPerms: [] },  // always visible to logged-in
]

const SIDEBAR_COLLAPSE_KEY = 'admin.sidebar.collapsed'

export function AppShell() {
  const { profile, logout } = useAuth()
  // Collapse the nav into a thin rail to free horizontal room for wide
  // content (e.g. the photoshoots table). Persisted across visits.
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === '1',
  )
  const toggleCollapsed = () =>
    setCollapsed((prev) => {
      const next = !prev
      localStorage.setItem(SIDEBAR_COLLAPSE_KEY, next ? '1' : '0')
      return next
    })
  const visibleNav = NAV.filter(
    (item) => item.requiredPerms.length === 0 || hasAnyPerm(profile, item.requiredPerms),
  )
  // "N new" badge on the Support entry — polled once a minute, only for
  // profiles that can see the inbox at all.
  const supportStats = useQuery({
    queryKey: ['admin', 'support', 'stats'],
    queryFn: () => request<AdminSupportStats>('/admin/support/stats'),
    enabled: hasAnyPerm(profile, [PERM.support_read]),
    refetchInterval: 60_000,
  })
  const newTickets = supportStats.data?.new ?? 0
  return (
    <div className="flex h-full w-full">
      <aside
        className={`flex shrink-0 flex-col border-r border-ink-100 bg-white transition-[width] duration-200 ${
          collapsed ? 'w-14' : 'w-60'
        }`}
      >
        <div
          className={`flex h-14 items-center border-b border-ink-100 ${
            collapsed ? 'justify-center px-0' : 'gap-2 px-4'
          }`}
        >
          {!collapsed && (
            <>
              <div className="h-6 w-6 shrink-0 rounded bg-brand-500" />
              <span className="flex-1 truncate font-semibold tracking-tight">Pinxie Admin</span>
            </>
          )}
          <button
            type="button"
            onClick={toggleCollapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-400 transition hover:bg-ink-50 hover:text-ink-700"
          >
            <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d={collapsed ? 'M8 5l5 5-5 5' : 'M12 5l-5 5 5 5'} />
            </svg>
          </button>
        </div>
        {!collapsed && (
        <nav className="flex-1 overflow-y-auto p-3">
          <ul className="flex flex-col gap-1">
            {visibleNav.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `block rounded-md px-3 py-2 text-sm font-medium transition ${
                      isActive
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-ink-500 hover:bg-ink-50 hover:text-ink-900'
                    }`
                  }
                >
                  {item.label}
                  {item.to === '/support' && newTickets > 0 && (
                    <span className="ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-brand-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
                      {newTickets}
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        )}
        {!collapsed && (
        <div className="border-t border-ink-100 p-3 text-xs text-ink-400">
          <div className="truncate text-ink-700">{profile?.email ?? '—'}</div>
          <div className="mt-0.5 inline-flex items-center gap-1 rounded bg-ink-50 px-1.5 py-0.5 font-mono">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {profile?.role ?? 'guest'}
          </div>
          <button
            type="button"
            onClick={() => { void logout() }}
            className="mt-2 w-full rounded-md border border-ink-100 px-2 py-1.5 text-left text-ink-500 transition hover:border-ink-200 hover:text-ink-900"
          >
            Sign out
          </button>
        </div>
        )}
      </aside>

      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  )
}
